/*
 * Portions of this file are derived from the Backstage project and are
 * licensed under the Apache License, Version 2.0. Modifications made as part
 * of the Refactory project are noted inline.
 */

import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as d3Zoom from 'd3-zoom';
import * as d3Selection from 'd3-selection';
import * as d3Shape from 'd3-shape';
import dagre from 'dagre';
import debounce from 'lodash/debounce';
import isFinite from 'lodash/isFinite';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { DependencyGraphTypes } from '@backstage/core-components';
import type { DependencyGraphProps } from '@backstage/core-components';

const WORKSPACE_ID = 'workspace';
const ARROW_MARKER_ID = 'user-story-arrow-marker';
const EDGE_TEST_ID = 'edge';
const LABEL_TEST_ID = 'label';
const NODE_TEST_ID = 'node';

type EdgePoint = { x: number; y: number };
type DagreEdge = { v: string; w: string };

type GraphEdge<EdgeData> = DependencyGraphTypes.DependencyEdge<EdgeData> & {
  label?: string;
  width?: number;
  height?: number;
  labeloffset?: number;
  labelpos?: DependencyGraphTypes.LabelPosition;
  minlen?: number;
  weight?: number;
  points?: EdgePoint[];
  x?: number;
  y?: number;
  relations?: string[];
};

type InternalGraphNode<NodeData> = DependencyGraphTypes.DependencyNode<NodeData> & {
  width?: number;
  height?: number;
  x?: number;
  y?: number;
};

export type GroupDefinition = {
  id: string;
  label: string;
  color: string;
  members: string[];
};

// Internal layout metadata that augments a group with concrete pixel bounds.
// Example shape:
// {
//   id: 'component:default/service-a',
//   label: 'Component · service-a',
//   color: '#E1F5FE',
//   members: ['component:default/service-a', 'component:default/service-a-worker'],
//   x: 120,
//   y: 48,
//   width: 420,
//  height: 260,
//   contentHeight: 220,
//   labelHeight: 30,
//   labelSpacing: 8,
//   padding: 32,
//   labelWidth: 180,
// }
type GroupBounds = GroupDefinition & {
  x: number;
  y: number;
  width: number;
  height: number;
  contentHeight: number;
  labelHeight: number;
  labelSpacing: number;
  padding: number;
  labelWidth: number;
};

const useEdgeStyles = makeStyles(
  theme => ({
    path: {
      strokeWidth: 1.3,
      stroke: theme.palette.textSubtle,
      fill: 'none',
      transition: `${theme.transitions.duration.shortest}ms`,
    },
    label: {
      transition: `${theme.transitions.duration.shortest}ms`,
    },
  }),
  { name: 'UserStoryDependencyGraphEdge' },
);

const useNodeStyles = makeStyles(
  theme => ({
    node: {
      transition: `${theme.transitions.duration.shortest}ms`,
    },
  }),
  { name: 'UserStoryDependencyGraphNode' },
);

const useGroupStyles = makeStyles(
  theme => ({
    groupRect: {
      fillOpacity: 0.08,
      strokeWidth: 1.5,
      padding: 20
    },
    groupLabelContainer: {
      fillOpacity: 0.28,
      strokeWidth: 0,
    },
    groupLabel: {
      ...theme.typography.caption,
      fontWeight: 600,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      fill: theme.palette.text.primary,
    },
    groupContainer: {
      pointerEvents: 'none',
    },
  }),
  { name: 'UserStoryDependencyGraphGroup' },
);

const DefaultLabel = ({
  edge,
}: DependencyGraphTypes.RenderLabelProps) => {
  const theme = useTheme();
  const graphEdge = edge as GraphEdge<unknown>;
  const relationText = graphEdge.relations?.length
    ? Array.from(new Set(graphEdge.relations)).join(', ')
    : graphEdge.label ?? '';

  return (
    <text
      className="user-story-default-label"
      textAnchor="middle"
      fill={theme.palette.textContrast}
    >
      {relationText}
    </text>
  );
};

const DefaultNode = ({
  node: { id },
}: DependencyGraphTypes.RenderNodeProps) => {
  const theme = useTheme();
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const textRef = useRef<SVGTextElement | null>(null);

  useLayoutEffect(() => {
    if (!textRef.current) {
      return;
    }

    let { height: renderedHeight, width: renderedWidth } =
      textRef.current.getBBox();
    renderedHeight = Math.round(renderedHeight);
    renderedWidth = Math.round(renderedWidth);

    if (renderedHeight !== height || renderedWidth !== width) {
      setWidth(renderedWidth);
      setHeight(renderedHeight);
    }
  }, [height, width]);

  const padding = 10;
  const paddedWidth = width + padding * 2;
  const paddedHeight = height + padding * 2;

  return (
    <g>
      <rect
        fill={theme.palette.primary.light}
        stroke={theme.palette.primary.light}
        width={paddedWidth}
        height={paddedHeight}
        rx={10}
      />
      <text
        ref={textRef}
        fill={theme.palette.primary.contrastText}
        y={paddedHeight / 2}
        x={paddedWidth / 2}
        textAnchor="middle"
        alignmentBaseline="middle"
      >
        {id}
      </text>
    </g>
  );
};

type EdgeComponentProps<EdgeData> = {
  id: DagreEdge;
  edge: GraphEdge<EdgeData>;
  render?: DependencyGraphTypes.RenderLabelFunction<EdgeData>;
  setEdge: (id: DagreEdge, edge: GraphEdge<EdgeData>) => unknown;
  curve: 'curveStepBefore' | 'curveMonotoneX';
  showArrowHeads?: boolean;
};

function Edge<EdgeData>({
  render = DefaultLabel,
  setEdge,
  id,
  edge,
  curve,
  showArrowHeads,
}: EdgeComponentProps<EdgeData>) {
  const { x = 0, y = 0, width, height, points } = edge;
  const labelProps: GraphEdge<EdgeData> = edge;
  const classes = useEdgeStyles();
  const labelRef = useRef<SVGGElement>(null);

  useLayoutEffect(() => {
    if (labelRef.current) {
      let { height: renderedHeight, width: renderedWidth } =
        labelRef.current.getBBox();
      renderedHeight = Math.round(renderedHeight);
      renderedWidth = Math.round(renderedWidth);

      if (renderedHeight !== height || renderedWidth !== width) {
        setEdge(id, {
          ...edge,
          height: renderedHeight,
          width: renderedWidth,
        });
      }
    }
  }, [edge, height, width, setEdge, id]);

  const createPath = useMemo(
    () =>
      d3Shape
        .line<EdgePoint>()
        .x(point => point.x)
        .y(point => point.y)
        .curve(d3Shape[curve]),
    [curve],
  );

  let path = '';

  if (points) {
    const finitePoints = points.filter(point =>
      isFinite(point.x) && isFinite(point.y),
    );
    path = createPath(finitePoints) || '';
  }

  return (
    <>
      {path ? (
        <path
          data-testid={EDGE_TEST_ID}
          className={classes.path}
          markerEnd={
            showArrowHeads ? `url(#${ARROW_MARKER_ID})` : undefined
          }
          d={path}
        />
      ) : null}
      {labelProps.label ? (
        <g
          ref={labelRef}
          data-testid={LABEL_TEST_ID}
          className={classes.label}
          transform={`translate(${x},${y})`}
        >
          {render({ edge: labelProps })}
        </g>
      ) : null}
    </>
  );
}

type NodeComponentProps<NodeData> = {
  node: InternalGraphNode<NodeData>;
  render?: DependencyGraphTypes.RenderNodeFunction<NodeData>;
  setNode: (id: string, node: InternalGraphNode<NodeData>) => unknown;
};

function Node<NodeData>({
  render = DefaultNode,
  setNode,
  node,
}: NodeComponentProps<NodeData>) {
  const { width = 0, height = 0, x = 0, y = 0 } = node;
  const classes = useNodeStyles();
  const nodeRef = useRef<SVGGElement | null>(null);

  useLayoutEffect(() => {
    if (!nodeRef.current) {
      return;
    }

    let { height: renderedHeight, width: renderedWidth } =
      nodeRef.current.getBBox();
    renderedHeight = Math.round(renderedHeight);
    renderedWidth = Math.round(renderedWidth);

    if (renderedHeight !== height || renderedWidth !== width) {
        setNode(node.id, {
          ...node,
          height: renderedHeight,
          width: renderedWidth,
        });
    }
  }, [node, width, height, setNode]);

  return (
    <g
      ref={nodeRef}
      data-testid={NODE_TEST_ID}
      className={classes.node}
      transform={`translate(${x - width / 2},${y - height / 2})`}
    >
      {render({ node })}
    </g>
  );
}

export type GroupedDependencyGraphProps<NodeData, EdgeData> =
  DependencyGraphProps<NodeData, EdgeData> & {
    groups?: GroupDefinition[];
    groupPadding?: number;
    groupHeaderHeight?: number;
    renderGroupLabel?: (group: GroupDefinition) => ReactNode;
  };

export function GroupedDependencyGraph<NodeData, EdgeData>(
  props: GroupedDependencyGraphProps<NodeData, EdgeData>,
) {
  const {
    edges,
    nodes,
    renderNode,
    direction = DependencyGraphTypes.Direction.TOP_BOTTOM,
    align,
    nodeMargin = 50,
    edgeMargin = 10,
    rankMargin = 50,
    paddingX = 0,
    paddingY = 0,
    acyclicer,
    ranker = DependencyGraphTypes.Ranker.NETWORK_SIMPLEX,
    labelPosition = DependencyGraphTypes.LabelPosition.RIGHT,
    labelOffset = 10,
    edgeRanks = 1,
    edgeWeight = 1,
    renderLabel,
    defs,
    zoom = 'enabled',
    curve = 'curveMonotoneX',
    showArrowHeads = false,
    fit = 'grow',
    groups = [],
    groupPadding = 32,
    groupHeaderHeight = 28,
    renderGroupLabel,
    ...svgProps
  } = props;

  const theme = useTheme();
  const groupClasses = useGroupStyles();
  const [containerWidth, setContainerWidth] = useState(100);
  const [containerHeight, setContainerHeight] = useState(100);
  const graph = useRef(new dagre.graphlib.Graph());
  const [graphWidth, setGraphWidth] = useState(
    graph.current.graph()?.width || 0,
  );
  const [graphHeight, setGraphHeight] = useState(
    graph.current.graph()?.height || 0,
  );
  const [graphNodes, setGraphNodes] = useState<string[]>([]);
  const [graphEdges, setGraphEdges] = useState<DagreEdge[]>([]);

  const setNodesAndEdges = useCallback(() => {
    const currentGraphNodes = graph.current.nodes();
    const currentGraphEdges = graph.current.edges();

    currentGraphNodes.forEach((nodeId: string) => {
      if (!nodes.some(node => node.id === nodeId)) {
        graph.current.removeNode(nodeId);
      }
    });

    currentGraphEdges.forEach((edgeRef: DagreEdge) => {
      if (!edges.some(edge => edge.from === edgeRef.v && edge.to === edgeRef.w)) {
        graph.current.removeEdge(edgeRef.v, edgeRef.w);
      }
    });

    nodes.forEach(node => {
      const existingNode = graph.current
        .nodes()
        .find((nodeId: string) => node.id === nodeId);

      if (existingNode && graph.current.node(existingNode)) {
        const { width, height, x, y } = graph.current.node(existingNode);
        graph.current.setNode(existingNode, {
          ...node,
          width,
          height,
          x,
          y,
        });
      } else {
        graph.current.setNode(node.id, { ...node, width: 0, height: 0 });
      }
    });

    edges.forEach(edge => {
      graph.current.setEdge(edge.from, edge.to, {
        ...edge,
        label: edge.label,
        width: 0,
        height: 0,
        labelpos: labelPosition,
        labeloffset: labelOffset,
        weight: edgeWeight,
        minlen: edgeRanks,
      });
    });
  }, [edges, nodes, labelPosition, labelOffset, edgeWeight, edgeRanks]);

  const updateGraph = useMemo(
    () =>
      debounce(
        () => {
          dagre.layout(graph.current);
          const { height, width } = graph.current.graph();
          setGraphWidth(Math.max(0, width ?? 0));
          setGraphHeight(Math.max(0, height ?? 0));
          setGraphNodes(graph.current.nodes());
          setGraphEdges(graph.current.edges());
        },
        250,
        { leading: true },
      ),
    [],
  );

  useEffect(() => {
    graph.current.setGraph({
      rankdir: direction,
      align,
      nodesep: nodeMargin,
      edgesep: edgeMargin,
      ranksep: rankMargin,
      marginx: paddingX,
      marginy: paddingY,
      acyclicer,
      ranker,
    });
    setNodesAndEdges();
    updateGraph();
    return updateGraph.cancel;
  }, [
    acyclicer,
    align,
    direction,
    edgeMargin,
    paddingX,
    paddingY,
    nodeMargin,
    rankMargin,
    ranker,
    setNodesAndEdges,
    updateGraph,
  ]);

  const setNode = useCallback(
    (id: string, node: InternalGraphNode<NodeData>) => {
      graph.current.setNode(id, node);
      updateGraph();
      return graph.current;
    },
    [updateGraph],
  );

  const setEdge = useCallback(
    (id: DagreEdge, edge: GraphEdge<EdgeData>) => {
      graph.current.setEdge(id.v, id.w, edge);
      updateGraph();
      return graph.current;
    },
    [updateGraph],
  );

  const groupBounds = useMemo<GroupBounds[]>(() => {
    if (!groups.length || !graphNodes.length) {
      return [];
    }

    return groups
      .map(group => {
        const members = group.members
          .map(memberId => graph.current.node(memberId))
          .filter(Boolean) as InternalGraphNode<NodeData>[];

        if (!members.length) {
          return undefined;
        }

        const minX = Math.min(
          ...members.map(node => (node.x ?? 0) - (node.width ?? 0) / 2),
        );
        const maxX = Math.max(
          ...members.map(node => (node.x ?? 0) + (node.width ?? 0) / 2),
        );
        const minY = Math.min(
          ...members.map(node => (node.y ?? 0) - (node.height ?? 0) / 2),
        );
        const maxY = Math.max(
          ...members.map(node => (node.y ?? 0) + (node.height ?? 0) / 2),
        );

        const bodyWidth = maxX - minX;
        const bodyHeight = maxY - minY;

        const baseHeight = bodyHeight + groupPadding * 2;
        const labelHeight = groupHeaderHeight ?? 28;
        const labelSpacing = labelHeight > 0 ? 12 : 0;
        const approximateCharWidth = 6.5;
        const estimatedLabelWidth = labelHeight > 0
          ? Math.max(120, group.label.length * approximateCharWidth + 32)
          : 0;

        let width = bodyWidth + groupPadding * 2;
        let x = minX - groupPadding;

        if (labelHeight > 0 && estimatedLabelWidth + groupPadding * 2 > width) {
          const requiredWidth = estimatedLabelWidth + groupPadding * 2;
          const extra = requiredWidth - width;
          width = requiredWidth;
          x -= extra / 2;
        }

        const height =
          baseHeight + (labelHeight > 0 ? labelHeight + labelSpacing : 0);

        const bounds: GroupBounds = {
          ...group,
          x,
          y: minY - groupPadding,
          width,
          height,
          contentHeight: baseHeight,
          labelHeight,
          labelSpacing,
          padding: groupPadding,
          labelWidth:
            labelHeight > 0
              ? Math.min(
                  Math.max(estimatedLabelWidth, width - groupPadding * 2 + 16),
                  width - groupPadding,
                )
              : 0,
        } satisfies GroupBounds;

        return bounds;
      })
      .filter((value): value is GroupBounds => Boolean(value));
  }, [groups, graphNodes, groupHeaderHeight, groupPadding]);

  const contentBounds = useMemo(() => {
    if (!graphNodes.length) {
      return {
        minX: 0,
        maxX: graphWidth,
        minY: 0,
        maxY: graphHeight,
      };
    }

    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    graphNodes.forEach(nodeId => {
      const node = graph.current.node(nodeId) as InternalGraphNode<NodeData>;
      if (!node) {
        return;
      }
      const halfWidth = (node.width ?? 0) / 2;
      const halfHeight = (node.height ?? 0) / 2;
      const nodeMinX = (node.x ?? 0) - halfWidth;
      const nodeMaxX = (node.x ?? 0) + halfWidth;
      const nodeMinY = (node.y ?? 0) - halfHeight;
      const nodeMaxY = (node.y ?? 0) + halfHeight;
      minX = Math.min(minX, nodeMinX);
      maxX = Math.max(maxX, nodeMaxX);
      minY = Math.min(minY, nodeMinY);
      maxY = Math.max(maxY, nodeMaxY);
    });

    groupBounds.forEach(group => {
      minX = Math.min(minX, group.x);
      maxX = Math.max(maxX, group.x + group.width);
      minY = Math.min(minY, group.y);
      maxY = Math.max(maxY, group.y + group.height);
    });

    if (!Number.isFinite(minX) || !Number.isFinite(maxX)) {
      return {
        minX: 0,
        maxX: graphWidth,
        minY: 0,
        maxY: graphHeight,
      };
    }

    return { minX, maxX, minY, maxY };
  }, [graphNodes, graphWidth, graphHeight, groupBounds]);

  const extraMargin = 32;
  const contentWidth = Math.max(0, contentBounds.maxX - contentBounds.minX);
  const contentHeight = Math.max(0, contentBounds.maxY - contentBounds.minY);
  const paddedContentWidth = contentWidth + extraMargin * 2;
  const paddedContentHeight = contentHeight + extraMargin * 2;
  const centerX = (contentBounds.minX + contentBounds.maxX) / 2;
  const centerY = (contentBounds.minY + contentBounds.maxY) / 2;
  const innerWidth = Math.max(paddedContentWidth, graphWidth);
  const innerHeight = Math.max(paddedContentHeight, graphHeight);
  const viewBoxX = centerX - innerWidth / 2;
  const viewBoxY = centerY - innerHeight / 2;

  const maxWidth = Math.max(innerWidth, containerWidth);
  const maxHeight = Math.max(innerHeight, containerHeight);
  const minHeight = Math.min(innerHeight, containerHeight);
  const scalableHeight = fit === 'grow' ? maxHeight : minHeight;

  const containerRef = useMemo(
    () =>
      debounce((node: SVGSVGElement | null) => {
        if (!node) {
          return;
        }

        const container = d3Selection.select(node);
        const workspace = d3Selection.select(
          node.getElementById(WORKSPACE_ID),
        );

        const enableZoom = () => {
          container.call(
            d3Zoom
              .zoom<SVGSVGElement, unknown>()
              .scaleExtent([1, Infinity])
              .on('zoom', event => {
                event.transform.x = Math.min(
                  0,
                  Math.max(
                    event.transform.x,
                    maxWidth - maxWidth * event.transform.k,
                  ),
                );
                event.transform.y = Math.min(
                  0,
                  Math.max(
                    event.transform.y,
                    maxHeight - maxHeight * event.transform.k,
                  ),
                );
                workspace.attr('transform', event.transform);
              }),
          );
        };

        if (zoom === 'enabled') {
          enableZoom();
        } else if (zoom === 'enable-on-click') {
          container.on('click', () => enableZoom());
        }

        const {
          width: newContainerWidth,
          height: newContainerHeight,
        } = node.getBoundingClientRect();
        if (containerWidth !== newContainerWidth) {
          setContainerWidth(newContainerWidth);
        }
        if (containerHeight !== newContainerHeight) {
          setContainerHeight(newContainerHeight);
        }
      }, 100),
    [containerHeight, containerWidth, maxWidth, maxHeight, zoom],
  );

  return (
    <svg
      ref={containerRef}
      {...svgProps}
      width="100%"
      height={scalableHeight}
      viewBox={`0 0 ${maxWidth} ${maxHeight}`}
    >
      <defs>
        <marker
          id={ARROW_MARKER_ID}
          viewBox="0 0 24 24"
          markerWidth="14"
          markerHeight="14"
          refX="16"
          refY="12"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path
            fill={theme.palette.textSubtle}
            d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"
          />
        </marker>
        {defs}
      </defs>

      <g id={WORKSPACE_ID}>
        <svg
          width={innerWidth}
          height={innerHeight}
          y={maxHeight / 2 - innerHeight / 2}
          x={maxWidth / 2 - innerWidth / 2}
          viewBox={`${viewBoxX} ${viewBoxY} ${innerWidth} ${innerHeight}`}
        >
          {groupBounds.map(group => {
            const { labelHeight, labelSpacing, padding, labelWidth } = group;
            const hasLabel = labelHeight > 0;
            const labelX = group.x + padding;
            const labelY =
              group.y + group.contentHeight + (labelSpacing > 0 ? labelSpacing / 2 : 0);

            return (
              <g key={group.id} className={groupClasses.groupContainer}>
                <rect
                  x={group.x}
                  y={group.y}
                  width={group.width}
                  height={group.height}
                  rx={16}
                  fill={group.color}
                  stroke={group.color}
                  className={groupClasses.groupRect}
                />
                {hasLabel ? (
                  <>
                    <rect
                      x={labelX}
                      y={labelY}
                      width={labelWidth}
                      height={labelHeight}
                      rx={labelHeight / 2}
                      fill={group.color}
                      className={groupClasses.groupLabelContainer}
                    />
                    <text
                      x={labelX + labelHeight / 2}
                      y={labelY + labelHeight * 0.7}
                      className={groupClasses.groupLabel}
                    >
                      {(renderGroupLabel && renderGroupLabel(group)) || group.label}
                    </text>
                  </>
                ) : null}
              </g>
            );
          })}

          {graphEdges.map(edgeRef => {
            const edge = graph.current.edge(edgeRef) as GraphEdge<EdgeData>;
            if (!edge) {
              return null;
            }

            return (
              <Edge
                key={`${edgeRef.v}-${edgeRef.w}`}
                id={edgeRef}
                setEdge={setEdge}
                render={renderLabel}
                edge={edge}
                curve={curve}
                showArrowHeads={showArrowHeads}
              />
            );
          })}

          {graphNodes.map(nodeId => {
            const node = graph.current.node(nodeId) as InternalGraphNode<NodeData>;
            if (!node) {
              return null;
            }

            return (
              <Node
                key={nodeId}
                setNode={setNode}
                render={renderNode}
                node={node}
              />
            );
          })}
        </svg>
      </g>
    </svg>
  );
}
